import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { MapPin } from 'lucide-react'

/**
 * Componente riutilizzabile per i campi del form indirizzo.
 * Usato sia nella pagina IndirizzoForm che nel dialog IndirizzoDialog.
 * 
 * @param {Object} props
 * @param {Object} props.formData - Dati del form
 * @param {Object} props.errors - Errori di validazione
 * @param {Function} props.onChange - Handler per cambio input
 * @param {Function} props.onSwitchChange - Handler per cambio switch default
 * @param {boolean} props.compact - Se true, layout più compatto per dialog (default: false)
 */
export default function IndirizzoFormFields({
  formData,
  errors,
  onChange,
  onSwitchChange,
  compact = false,
  idPrefix = '',
  showTipo = true,
  showDestinatario = true,
  showAddressLabel = true,
}) {
  const fieldId = (name) => `${idPrefix}${name}`

  return (
    <div className={compact ? 'space-y-4' : 'space-y-6'}>
      {/* Tipo Indirizzo */}
      {showTipo && <div className="space-y-2">
        <Label>Tipo Indirizzo</Label>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="tipo"
              value="fatturazione"
              checked={formData.tipo === 'fatturazione'}
              onChange={onChange}
              className="w-4 h-4"
            />
            <span>Fatturazione</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="tipo"
              value="spedizione"
              checked={formData.tipo === 'spedizione'}
              onChange={onChange}
              className="w-4 h-4"
            />
            <span>Spedizione</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="tipo"
              value="entrambi"
              checked={formData.tipo === 'entrambi'}
              onChange={onChange}
              className="w-4 h-4"
            />
            <span>Fatturazione e Spedizione</span>
          </label>
        </div>
      </div>}

      {/* Destinatario */}
      {showDestinatario && formData.tipo !== 'fatturazione' && <div className="space-y-2">
        <Label htmlFor={fieldId('destinatario')}>Nome Destinatario (opzionale)</Label>
        <Input
          id={fieldId('destinatario')}
          name="destinatario"
          value={formData.destinatario}
          onChange={onChange}
          placeholder="Se diverso dal cliente"
        />
        <p className="text-sm text-muted-foreground">
          Lascia vuoto per usare il nome del cliente
        </p>
      </div>}

      {/* Indirizzo */}
      <div className="space-y-4">
        {showAddressLabel && <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          <Label className="text-base font-medium">Indirizzo</Label>
        </div>}

        <div className="grid gap-4 md:grid-cols-3">
          <div className="md:col-span-2 space-y-2">
            <Label htmlFor={fieldId('via')}>
              Via/Piazza <span className="text-red-500">*</span>
            </Label>
            <Input
              id={fieldId('via')}
              name="via"
              value={formData.via}
              onChange={onChange}
              placeholder="Via Roma"
            />
            {errors.via && (
              <p className="text-sm text-red-500">{errors.via}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor={fieldId('civico')}>Numero Civico</Label>
            <Input
              id={fieldId('civico')}
              name="civico"
              value={formData.civico}
              onChange={onChange}
              placeholder="123"
            />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor={fieldId('cap')}>
              CAP <span className="text-red-500">*</span>
            </Label>
            <Input
              id={fieldId('cap')}
              name="cap"
              value={formData.cap}
              onChange={onChange}
              placeholder="80100"
              maxLength={5}
            />
            {errors.cap && (
              <p className="text-sm text-red-500">{errors.cap}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor={fieldId('citta')}>
              Città <span className="text-red-500">*</span>
            </Label>
            <Input
              id={fieldId('citta')}
              name="citta"
              value={formData.citta}
              onChange={onChange}
              placeholder="Napoli"
            />
            {errors.citta && (
              <p className="text-sm text-red-500">{errors.citta}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor={fieldId('provincia')}>
              Provincia <span className="text-red-500">*</span>
            </Label>
            <Input
              id={fieldId('provincia')}
              name="provincia"
              value={formData.provincia}
              onChange={onChange}
              placeholder="NA"
              maxLength={2}
              className="uppercase"
            />
            {errors.provincia && (
              <p className="text-sm text-red-500">{errors.provincia}</p>
            )}
            <p className="text-sm text-muted-foreground">2 lettere (es: NA, RM)</p>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor={fieldId('nazione')}>Nazione</Label>
          <Input
            id={fieldId('nazione')}
            name="nazione"
            value={formData.nazione}
            onChange={onChange}
            placeholder="IT"
            maxLength={2}
            className="uppercase w-24"
          />
          <p className="text-sm text-muted-foreground">Codice ISO (es: IT, FR, DE)</p>
        </div>
      </div>

      {/* Note */}
      <div className="space-y-2">
        <Label htmlFor={fieldId('note')}>Note per il Corriere</Label>
        <Textarea
          id={fieldId('note')}
          name="note"
          value={formData.note}
          onChange={onChange}
          placeholder="Citofono, piano, orari preferiti..."
          rows={compact ? 2 : 3}
        />
      </div>

      {/* Indirizzo Predefinito */}
      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor={fieldId('default')}>Indirizzo Predefinito</Label>
          <p className="text-sm text-muted-foreground">
            Se attivo, questo sarà l&apos;indirizzo predefinito del cliente
          </p>
        </div>
        <Switch
          id={fieldId('default')}
          checked={formData.default}
          onCheckedChange={onSwitchChange}
        />
      </div>
    </div>
  )
}
